/**
 * HealthRing — circular score gauge (0–100) with label and tooltip.
 * Pure CSS/SVG, no external lib.
 */

import React, { useMemo, useState } from 'react';
import { cn } from '@/lib/utils';

export interface HealthRingProps {
  label: string;
  score: number | null;
  /** Short explanation of how score is computed */
  reasons?: string[];
  onClick?: () => void;
  className?: string;
}

const RING_SIZE = 80;
const STROKE_WIDTH = 7;
const RADIUS = (RING_SIZE - STROKE_WIDTH) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

function scoreColor(s: number): string {
  if (s >= 75) return '#22C55E'; // green
  if (s >= 50) return '#F59E0B'; // amber
  return '#EF4444'; // red
}

export const HealthRing: React.FC<HealthRingProps> = ({
  label,
  score,
  reasons,
  onClick,
  className,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const strokeDashoffset = useMemo(() => {
    if (score === null) return CIRCUMFERENCE;
    const pct = Math.max(0, Math.min(100, score)) / 100;
    return CIRCUMFERENCE * (1 - pct);
  }, [score]);

  const color = score !== null ? scoreColor(score) : '#6B7280';

  return (
    <div
      className={cn(
        'flex flex-col items-center gap-1 relative',
        onClick && 'cursor-pointer',
        className,
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <svg
        width={RING_SIZE}
        height={RING_SIZE}
        viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
        className="transform -rotate-90"
      >
        {/* Background ring */}
        <circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke="currentColor"
          strokeWidth={STROKE_WIDTH}
          className="text-muted/30"
        />
        {/* Score arc */}
        <circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke={color}
          strokeWidth={STROKE_WIDTH}
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className="transition-all duration-700 ease-out"
        />
      </svg>

      {/* Score number centered inside ring */}
      <div className="absolute flex items-center justify-center" style={{ width: RING_SIZE, height: RING_SIZE, top: 0 }}>
        <span
          className={cn(
            'text-base font-bold',
            score === null ? 'text-muted-foreground' : 'text-foreground',
          )}
        >
          {score !== null ? score : 'N/A'}
        </span>
      </div>

      {/* Label below */}
      <span className="text-[11px] font-medium text-muted-foreground text-center leading-tight">
        {label}
      </span>

      {/* Tooltip */}
      {showTooltip && reasons && reasons.length > 0 && (
        <div className="absolute top-full mt-2 z-50 w-48 p-2 rounded-md border bg-popover text-popover-foreground shadow-md text-[11px] leading-relaxed">
          <p className="font-medium mb-1">How computed:</p>
          <ul className="list-disc pl-3 space-y-0.5">
            {reasons.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
